"use client"

import Link from "next/link"
import { motion, useReducedMotion } from "framer-motion"
import { EventCard } from "@/components/event-card"
import { Button } from "@/components/ui/button"
import { getMarketingDemoEventCardProps } from "@/lib/demo/public-demo-event"

const easeOut = [0.22, 1, 0.36, 1] as const

const demoCard = getMarketingDemoEventCardProps()

export function MarketingProductJourney() {
  const reduceMotion = useReducedMotion()

  return (
    <section className="relative w-full min-w-0 bg-card/[0.14] py-24 sm:py-30">
      <div className="mx-auto grid max-w-[71rem] gap-10 lg:grid-cols-12 lg:items-start lg:gap-8">
        <motion.div
          className="lg:col-span-5 lg:pt-3"
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: reduceMotion ? 0 : 0.45, ease: easeOut }}
        >
          <motion.p
            className="type-marketing-eyebrow mb-3 text-left"
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: reduceMotion ? 0 : 0.45, ease: easeOut }}
          >
            Proof
          </motion.p>
          <motion.h2
            className="type-display-title max-w-[18ch] text-balance !mb-3 text-left sm:!mb-4"
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              duration: reduceMotion ? 0 : 0.52,
              delay: reduceMotion ? 0 : 0.04,
              ease: easeOut,
            }}
          >
            From invitation to what you keep
          </motion.h2>
          <motion.p
            className="type-hero-subtitle max-w-[34ch] text-left text-foreground/82"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              duration: reduceMotion ? 0 : 0.48,
              delay: reduceMotion ? 0 : 0.08,
              ease: easeOut,
            }}
          >
            One page carries the invitation, the evening, and what remains after.
          </motion.p>
          <div className="mt-6 flex justify-start">
            <Button
              variant="outline"
              size="lg"
              className="h-11 rounded-full px-8 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:opacity-95"
              asChild
            >
              <Link href="/demo">Open demo</Link>
            </Button>
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-7 lg:pl-3"
          initial={reduceMotion ? false : { opacity: 0, y: 24, scale: 0.985 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{
            duration: reduceMotion ? 0 : 0.65,
            delay: reduceMotion ? 0 : 0.1,
            ease: easeOut,
          }}
        >
          <div className="overflow-hidden rounded-[1.6rem] border border-border/50 bg-card/35 p-4 sm:rounded-[2rem] sm:p-6">
            <div className="mx-auto max-w-[min(100%,70rem)]">
              <EventCard
                {...demoCard}
                coverPriority={false}
                surfaceClassName="border-border/45 shadow-none ring-1 ring-inset ring-white/10"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
